import React, { useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Empty, Tabs } from 'antd';
import { useAuthStore } from '../../../store';
import { canReadModule } from '../../../components/nav/SideMenuItems';
import PageHeaderAction from '../../../components/PageHeaderAction';
import PlanningInputsPage from '../planning-inputs';
import PlanningBoardPage from '../planning-board';
import ProderProgressPage from '../proder-progress';

/**
 * Planning, one page. Tabs follow the planning flow (Edward: "the setup
 * should be first right , capacity, then schedule , then proders"):
 * Setup -> Capacity -> Schedule -> Proders. Capacity is left out until it is
 * built rather than shown empty. Each tab is gated by its own module, the
 * same read rule as the side menu, and has its own url so refresh and
 * bookmarks land on it. With no tab in the url the page opens on the tab
 * this browser used last, else Proders -- the daily view.
 */
const TABS = [
  { key: 'setup', label: 'Setup', module: 'PIN' },
  { key: 'schedule', label: 'Schedule (legacy board)', module: 'PBD' },
  { key: 'proders', label: 'Proders', module: 'PRP' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const LAST_TAB = 'mmd.planning.lastTab';

function readLastTab(): string | null {
  try {
    return window.localStorage.getItem(LAST_TAB);
  } catch {
    return null;
  }
}

function writeLastTab(tab: string) {
  try {
    window.localStorage.setItem(LAST_TAB, tab);
  } catch {
    // private mode or blocked storage: the page still works, it just
    // opens on the default tab next time
  }
}

const PlanningPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const allowed = useMemo(
    () => TABS.filter((t) => canReadModule(user, t.module)),
    [user],
  );

  const current = allowed.find((t) => t.key === tab);

  useEffect(() => {
    if (current) writeLastTab(current.key);
  }, [current]);

  if (allowed.length === 0) {
    return (
      <div className={'page-wrapper'}>
        <PageHeaderAction moduleName={'Planning'} action={null} />
        <Empty
          className={'my-8'}
          description={'You do not have access to any planning module.'}
        />
      </div>
    );
  }

  if (!current) {
    const last = readLastTab();
    const fallback =
      allowed.find((t) => t.key === last) ??
      allowed.find((t) => t.key === 'proders') ??
      allowed[0];
    return <Navigate to={`/production/planning/${fallback.key}`} replace />;
  }

  const render = (key: TabKey) => {
    switch (key) {
      case 'setup':
        return <PlanningInputsPage embedded />;
      case 'schedule':
        return <PlanningBoardPage embedded />;
      case 'proders':
        return <ProderProgressPage embedded />;
    }
  };

  return (
    <div className={'page-wrapper'}>
      <PageHeaderAction moduleName={'Planning'} action={null} />
      <Tabs
        activeKey={current.key}
        destroyInactiveTabPane
        onChange={(key) => navigate(`/production/planning/${key}`)}
        items={allowed.map((t) => ({
          key: t.key,
          label: t.label,
          children: t.key === current.key ? render(t.key) : null,
        }))}
      />
    </div>
  );
};

export default PlanningPage;
